import React from "react";
import PropTypes from "prop-types";
import { FormattedMessage } from "react-intl";
import styled from "@emotion/styled";
import { format, parseISO } from "date-fns";
import { breakpoints, colors, fontSize, margins } from "style";
import { H3 } from "components/Text";
import { Row } from "components/Containers";
import { TextLoader } from "components/Loader";
import Icon, { ICON_TYPE } from "components/Icon";

/**
 * DailyReportHeader
 * Displays the header section with date and participation stats
 *
 * @param {string}    date                    // The date for this report (YYYY-MM-DD)
 * @param {Object}    participationMetrics    // Participation stats regarding the date
 * @param {boolean}   loading                 // Whether the header is loading
 * @param {boolean}   isExpanded              // Whether the report card is expanded
 */
const DailyReportHeader = ({ date, participationMetrics, loading, isExpanded, ...props }) => {
  if (loading) return <Loader />;

  const dateObj = parseISO(date);
  const dayOfWeek = format(dateObj, "EEEE");
  const formattedDate = format(dateObj, "MMMM d, yyyy");

  return (
    <Header isExpanded={isExpanded} {...props}>
      <RowWithStats align="flex-start" noWrap>
        <H3>
          <FormattedMessage id="Squads.Squad.DailyReport.Header" values={{ day: dayOfWeek, date: formattedDate }} />
        </H3>
        <RightContainer>
          <ParticipationStats>
            <ParticipationText>
              <FormattedMessage
                id="Squads.Squad.DailyReport.Stats.Participation"
                values={{
                  completed: participationMetrics?.completed,
                  total: participationMetrics?.total,
                  percentage: participationMetrics?.percentage,
                }}
              />
            </ParticipationText>
          </ParticipationStats>
          <Icon type={isExpanded ? ICON_TYPE.chevronUp : ICON_TYPE.chevronDown} />
        </RightContainer>
      </RowWithStats>
    </Header>
  );
};

const Loader = () => (
  <Header>
    <RowWithStats align="flex-start" noWrap>
      <div>
        <H3>
          <TextLoader width="45rem" />
        </H3>
      </div>
      <RightContainer>
        <ParticipationText>
          <TextLoader width="20rem" />
        </ParticipationText>
        <TextLoader width="5rem" />
      </RightContainer>
    </RowWithStats>
  </Header>
);

const RightContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${margins.xxsmall};
`;

const Header = styled.div`
  position: sticky;
  top: 0;
  z-index: 10;
  background-color: ${colors.white};
  border-bottom: 1px ${({ isExpanded }) => `${isExpanded ? colors.grayAnatomyLight4 : colors.transparent} solid`};
  margin-right: ${margins.negative};
  padding-right: ${margins.normal};
  padding-top: ${margins.xxsmall};
  padding-bottom: ${({ isExpanded }) => (isExpanded ? margins.xxsmall : 0)};
  margin-left: -3px;
  cursor: pointer;

  @media (max-width: ${breakpoints.portrait}) {
    margin-right: -2.5rem;
    padding-right: 2.5rem;
  }

  @media (max-width: ${breakpoints.mobile}) {
    margin-right: -2rem;
    padding-right: 2rem;
  }
`;

const RowWithStats = styled(Row)`
  justify-content: space-between;
  align-items: flex-start;
  align-items: anchor-center;
`;

const ParticipationStats = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const ParticipationText = styled.span`
  font-size: ${fontSize.small};
  font-weight: 600;
  color: ${colors.grayAnatomyDark1};
`;

DailyReportHeader.propTypes = {
  date: PropTypes.string.isRequired,
  participationMetrics: PropTypes.shape({
    total: PropTypes.number,
    completed: PropTypes.number,
    percentage: PropTypes.number,
  }),
  loading: PropTypes.bool,
  isExpanded: PropTypes.bool,
};

export default DailyReportHeader;
